import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/card";
import { useToast } from "../../../contexts/ToastContext";
import { Trash2, Edit2, Loader2, Save, X, Plus } from "lucide-react";

interface VideoTutorial {
  id: string;
  category: string;
  title: string;
  youtube_url: string;
  thumbnail_type: 'youtube' | 'custom';
  custom_thumbnail_url: string | null;
  sort_order?: number;
  category_order?: number;
  created_at?: string;
}

export function ManageVideoTutorials() {
  const [tutorials, setTutorials] = useState<VideoTutorial[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<VideoTutorial>>({
    thumbnail_type: 'youtube',
    category: '',
    title: '',
    youtube_url: '',
    custom_thumbnail_url: '',
    sort_order: 0,
    category_order: 0
  });
  const { showToast } = useToast();

  const fetchTutorials = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_tutorials')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        // If the table doesn't exist yet, we will get an error, which we can ignore gracefully.
        if (error.code !== '42P01') {
           showToast({ type: 'error', title: 'Error fetching tutorials', message: error.message });
        }
      } else {
        setTutorials(data || []);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTutorials();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      thumbnail_type: 'youtube',
      category: '',
      title: '',
      youtube_url: '',
      custom_thumbnail_url: '',
      sort_order: 0,
      category_order: 0
    });
    setIsEditing(null);
  };

  const getYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getThumbnailPreview = () => {
    if (formData.thumbnail_type === 'custom' && formData.custom_thumbnail_url) {
      return formData.custom_thumbnail_url;
    }
    if (formData.thumbnail_type === 'youtube' && formData.youtube_url) {
      const id = getYouTubeId(formData.youtube_url);
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    return '';
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.category || !formData.title || !formData.youtube_url) {
        throw new Error("Category, Title, and YouTube URL are required");
      }

      if (isEditing) {
        const { error } = await supabase
          .from('video_tutorials')
          .update({
            category: formData.category,
            title: formData.title,
            youtube_url: formData.youtube_url,
            thumbnail_type: formData.thumbnail_type,
            custom_thumbnail_url: formData.thumbnail_type === 'custom' ? formData.custom_thumbnail_url : null,
            sort_order: formData.sort_order || 0,
            category_order: formData.category_order || 0
          })
          .eq('id', isEditing);

        if (error) throw error;
        showToast({ type: 'success', title: 'Tutorial updated', message: 'Video tutorial updated successfully' });
      } else {
        const { error } = await supabase
          .from('video_tutorials')
          .insert({
            category: formData.category,
            title: formData.title,
            youtube_url: formData.youtube_url,
            thumbnail_type: formData.thumbnail_type,
            custom_thumbnail_url: formData.thumbnail_type === 'custom' ? formData.custom_thumbnail_url : null,
            sort_order: formData.sort_order || 0,
            category_order: formData.category_order || 0
          });

        if (error) throw error;
        showToast({ type: 'success', title: 'Tutorial added', message: 'Video tutorial added successfully' });
      }

      fetchTutorials();
      resetForm();
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this tutorial?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('video_tutorials').delete().eq('id', id);
      if (error) throw error;
      showToast({ type: 'success', title: 'Tutorial deleted', message: 'Video tutorial deleted successfully' });
      fetchTutorials();
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tutorial: VideoTutorial) => {
    setIsEditing(tutorial.id);
    setFormData({ ...tutorial });
  };

  const previewUrl = getThumbnailPreview();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Video Tutorials</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSave} className="space-y-4 border p-4 rounded-lg bg-card/50">
          <h3 className="font-semibold text-lg">{isEditing ? 'Edit Tutorial' : 'Add New Tutorial'}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Category / Menu Title</label>
              <Input
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                placeholder="e.g. Market Analysis"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category Order (Urutan Kategori)</label>
              <Input
                name="category_order"
                type="number"
                value={formData.category_order ?? 0}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Video Title</label>
              <Input
                name="title"
                value={formData.title || ''}
                onChange={handleInputChange}
                placeholder="e.g. Accumulation Distribution"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">YouTube URL</label>
              <Input
                name="youtube_url"
                value={formData.youtube_url || ''}
                onChange={handleInputChange}
                placeholder="https://youtube.com/watch?v=..."
                required
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Thumbnail Source</label>
              <select
                name="thumbnail_type"
                value={formData.thumbnail_type || 'youtube'}
                onChange={handleInputChange}
                className="w-full h-9 flex rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option className="bg-background text-foreground" value="youtube">Auto (YouTube Thumbnail)</option>
                <option className="bg-background text-foreground" value="custom">Custom Image Link</option>
              </select>
            </div>

            {formData.thumbnail_type === 'custom' && (
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">Custom Thumbnail URL</label>
                <Input
                  name="custom_thumbnail_url"
                  value={formData.custom_thumbnail_url || ''}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.png"
                  required={formData.thumbnail_type === 'custom'}
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Video Order (Urutan Video)</label>
              <Input
                name="sort_order"
                type="number"
                value={formData.sort_order ?? 0}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          {previewUrl && (
            <div className="mt-4">
              <label className="text-sm font-medium block mb-2">Thumbnail Preview:</label>
              <img src={previewUrl} alt="Preview" className="w-48 h-auto aspect-video object-cover rounded-lg border" />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            {isEditing && (
              <Button type="button" variant="outline" onClick={resetForm}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (isEditing ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {isEditing ? 'Update' : 'Add Tutorial'}
            </Button>
          </div>
        </form>

        <div className="mt-8 border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Title</th>
                  <th className="px-4 py-3 font-semibold">Video URL</th>
                  <th className="px-4 py-3 font-semibold">Order</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tutorials.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No video tutorials found. Please add some!
                    </td>
                  </tr>
                ) : (
                  tutorials.map((tutorial) => (
                    <tr key={tutorial.id} className="group transition-colors hover:bg-muted/50">
                      <td className="px-4 py-3">
                        {tutorial.category}
                        <div className="text-[10px] text-muted-foreground">Cat Order: {tutorial.category_order || 0}</div>
                      </td>
                      <td className="px-4 py-3">{tutorial.title}</td>
                      <td className="px-4 py-3">
                        <a href={tutorial.youtube_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                          {tutorial.youtube_url}
                        </a>
                      </td>
                      <td className="px-4 py-3">{tutorial.sort_order || 0}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(tutorial)}>
                          <Edit2 className="w-4 h-4 text-muted-foreground hover:text-primary" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(tutorial.id)}>
                          <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
